import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, BadgeDollarSign, XCircle, FileText, ShieldCheck } from "lucide-react";
import { api, errMsg, fmtDateTime, money, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Empty, ConfirmDialog } from "@/components/admin/ui";
import { StatusBadge } from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/typed";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const FILTERS = [["", "All"], ["awaiting_approval", "Awaiting approval"], ["pending_payment", "Pending payment"], ["invoiced", "Invoiced, unpaid"], ["issued", "Issued"], ["cancelled", "Cancelled"]];
type Action = { kind: "issue" | "paid" | "cancel"; order: any } | null;

export default function Orders() {
  const { can } = useAuth();
  const focus = useSearchParams()[0].get("order");
  const [filter, setFilter] = useState(focus ? "" : "awaiting_approval");
  const [orders, setOrders] = useState<any[]>([]);
  const [action, setAction] = useState<Action>(null);
  const [conv, setConv] = useState<any>(null);
  const [addr, setAddr] = useState("");
  const convert = async () => {
    try { await api.post(`/admin/orders/${conv.id}/to-invoice`, { address: addr }); toast.success(`${conv.number} switched to an invoice order`); setConv(null); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const load = useCallback(() => api.get("/admin/orders", { params: { status: filter || undefined } }).then((r) => setOrders(r.data)), [filter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (focus) { document.querySelector(`[data-testid="order-row-${focus}"]`)?.scrollIntoView({ block: "center" }); } }, [focus, orders]);

  const invoice = (o: any) => downloadFile(`/admin/orders/${o.id}/invoice.pdf`, `invoice-${o.number}.pdf`).catch((e) => toast.error(errMsg(e)));
  const run = async (note: string) => {
    if (!action) return;
    const o = action.order;
    try {
      if (action.kind === "issue") {
        const { data } = await api.post(`/admin/orders/${o.id}/approve-issue`);
        toast.success(`${data.codes_issued} codes issued${data.email_status === "sent" ? " and emailed" : " — email failed, resend from the organization page"}`);
        if (o.payment_method === "invoice") invoice(o);
      } else if (action.kind === "paid") {
        const { data } = await api.post(`/admin/orders/${o.id}/mark-paid`, { note });
        toast.success(data.receipt_status === "sent" ? `Marked paid. PAID receipt emailed to ${o.contact_email}` : data.receipt_status === "failed" ? "Marked paid, but the receipt email failed. Download the receipt and send it manually" : "Order marked as paid");
      } else {
        await api.post(`/admin/orders/${o.id}/cancel`, { note });
        toast.success("Order cancelled");
      }
      load();
    } catch (e) {
      toast.error(errMsg(e));
      throw e;
    }
  };

  const approver = can("approve_orders");
  return (
    <div>
      <PageHeader title="Orders" subtitle="Card orders issue codes automatically. Approve invoice orders to generate and email their codes." />
      <Tabs value={filter} onValueChange={setFilter} className="mb-5">
        <TabsList className="h-auto flex-wrap rounded-xl bg-[#F1EFEA] p-1">
          {FILTERS.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`orders-filter-${v || "all"}`} className="rounded-lg px-4 py-2 text-sm font-semibold">{l}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      <Card>
        {orders.length ? (
          <div className="overflow-x-auto">
            <table className="tbl" data-testid="orders-table">
              <thead><tr><th>Order</th><th>Organization</th><th>Qty</th><th>Amount</th><th>Status</th><th>Placed</th><th className="text-right">Actions</th></tr></thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} data-testid={`order-row-${o.id}`} className={o.id === focus ? "bg-[#FEF3C7]" : undefined}>
                    <td className="font-mono text-[13px] font-semibold">{o.number}</td>
                    <td>
                      <Link to={`/admin/organizations/${o.org_id}`} className="font-semibold text-[#1B365D] hover:underline">{o.org_name}</Link>
                      <p className="text-xs text-[#94A3B8]">{o.contact_name} · {o.contact_email}</p>
                    </td>
                    <td className="font-mono">{o.quantity}</td>
                    <td className="font-mono">{money(o.amount)}</td>
                    <td>
                      <StatusBadge status={o.payment_status === "invoice_requested" && o.status === "awaiting_approval" ? "invoice_review" : o.status} testId={`order-status-${o.id}`} />
                      {o.payment_method === "invoice" && (
                        <p className={`mt-1 text-xs font-semibold ${o.payment_status === "paid" ? "text-[#15803D]" : "text-[#B45309]"}`} data-testid={`order-invoice-status-${o.id}`}>
                          {o.payment_status === "invoice_requested" ? "Invoice requested" : o.payment_status === "invoiced" ? "Invoiced, unpaid" : "Invoice paid"}
                          {o.reminder_count > 0 && o.payment_status === "invoiced" && <span className="block font-normal text-[#64748B]" data-testid={`order-reminders-${o.id}`}>{o.reminder_count} reminder{o.reminder_count > 1 ? "s" : ""} sent</span>}
                          {o.receipt_status === "sent" && <span className="block font-normal text-[#64748B]" data-testid={`order-receipt-${o.id}`}>Receipt emailed</span>}
                        </p>
                      )}
                    </td>
                    <td className="text-xs text-[#64748B]">{fmtDateTime(o.created_at)}</td>
                    <td>
                      <div className="flex flex-wrap justify-end gap-2 [&_button]:whitespace-nowrap">
                        {approver && o.status === "awaiting_approval" && (
                          <button className="btn-primary btn-sm" data-testid="admin-issue-codes-button" onClick={() => setAction({ kind: "issue", order: o })}><CheckCircle2 className="h-4 w-4" /> Approve & issue</button>
                        )}
                        {approver && o.payment_auth && (
                          <button className="btn-secondary btn-sm" data-testid={`order-auth-pdf-${o.id}`} title="Signed credit card authorization, for charge disputes" onClick={() => downloadFile(`/admin/orders/${o.id}/authorization.pdf`, `card-authorization-${o.number}.pdf`).catch((e) => toast.error(errMsg(e)))}><FileText className="h-4 w-4" /> Card authorization</button>
                        )}
                        {approver && o.payment_auth && (
                          <button className="btn-secondary btn-sm" data-testid={`order-dispute-packet-${o.id}`} title="One PDF with the card authorization, paid receipt, code usage, email log and order activity" onClick={() => downloadFile(`/admin/orders/${o.id}/dispute-packet.pdf`, `dispute-packet-${o.number}.pdf`).catch((e) => toast.error(errMsg(e)))}><ShieldCheck className="h-4 w-4" /> Dispute packet</button>
                        )}
                        {approver && o.payment_method === "invoice" && o.status !== "cancelled" && (
                          <button className="btn-secondary btn-sm" data-testid={`order-invoice-pdf-${o.id}`} onClick={() => invoice(o)}><FileText className="h-4 w-4" /> {o.payment_status === "paid" ? "Receipt PDF" : "Invoice PDF"}</button>
                        )}
                        {approver && o.status === "pending_payment" && (
                          <button className="btn-secondary btn-sm" data-testid={`order-to-invoice-${o.id}`} onClick={() => { setConv(o); setAddr(o.address || ""); }}><FileText className="h-4 w-4" /> Switch to invoice</button>
                        )}
                        {approver && (o.status === "pending_payment" || o.payment_status === "invoiced") && (
                          <button className="btn-secondary btn-sm" data-testid={`order-mark-paid-${o.id}`} onClick={() => setAction({ kind: "paid", order: o })}><BadgeDollarSign className="h-4 w-4" /> Mark paid</button>
                        )}
                        {approver && ["pending_payment", "awaiting_approval"].includes(o.status) && (
                          <button className="btn-secondary btn-sm text-[#B91C1C]" data-testid={`order-cancel-${o.id}`} onClick={() => setAction({ kind: "cancel", order: o })}><XCircle className="h-4 w-4" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty text="No orders in this view." />}
      </Card>
      <Dialog open={!!conv} onOpenChange={(v) => !v && setConv(null)}>
        <DialogContent className="rounded-2xl" data-testid="order-to-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Switch {conv?.number} to an invoice?</DialogTitle>
            <DialogDescription className="text-[15px]">The unfinished card checkout will be closed. The order moves to Awaiting approval, where you can download the Invoice PDF and approve it to issue the codes.</DialogDescription>
          </DialogHeader>
          <label className="block"><span className="field-label">Mailing address for the invoice</span>
            <textarea className="field-input h-auto py-3" rows={3} value={addr} onChange={(e) => setAddr(e.target.value)} data-testid="order-to-invoice-address" /></label>
          <DialogFooter className="gap-2">
            <button className="btn-secondary btn-sm" onClick={() => setConv(null)} data-testid="order-to-invoice-cancel">Cancel</button>
            <button className="btn-primary btn-sm" disabled={addr.trim().length < 8} onClick={convert} data-testid="order-to-invoice-confirm">Switch to invoice</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={!!action}
        onOpenChange={(o) => !o && setAction(null)}
        testId="order-action-dialog"
        title={action?.kind === "issue" ? `Issue ${action.order.quantity} codes?` : action?.kind === "paid" ? "Mark this order as paid?" : "Cancel this order?"}
        description={
          action?.kind === "issue" ? `Codes will be generated for ${action.order.org_name} and emailed to ${action.order.contact_email}.${action.order.payment_method === "invoice" ? " The order will be marked Invoiced, unpaid until you mark it paid. Remember to mail the invoice." : ""}`
            : action?.kind === "paid" ? (action.order.payment_status === "invoiced" ? `Use this when the invoice payment (e.g. a check) has been received. A PAID receipt PDF will be emailed to ${action.order.contact_email}.` : "Use this when accounting has confirmed payment outside of online checkout.") : "The organization will not receive codes for this order."
        }
        confirmLabel={action?.kind === "issue" ? "Approve & issue" : action?.kind === "paid" ? "Mark paid" : "Cancel order"}
        withNote={action?.kind !== "issue"}
        danger={action?.kind === "cancel"}
        onConfirm={run}
      />
    </div>
  );
}
